"use strict";

var React = require("react");
var useEffect = React.useEffect;
var useRef = React.useRef;
var useState = React.useState;
var useMemo = React.useMemo;
var useCallback = React.useCallback;
var createElement = React.createElement;
var useLayoutEffect = (typeof window !== "undefined") ? (React.useLayoutEffect || useEffect) : useEffect;

function noop() {}

function isObj(v) { return v && typeof v === "object" && !Array.isArray(v); }

function defaultSpringConfig() {
  return { stiffness: 100, damping: 10, mass: 1, velocity: 0, dampingRatio: 0, restSpeed: 0.01, restDelta: 0.01 };
}
function mergeSpringConfig(userCfg) {
  var c = defaultSpringConfig();
  if (!userCfg) return c;
  if (typeof userCfg.stiffness === "number") c.stiffness = userCfg.stiffness;
  if (typeof userCfg.damping === "number") c.damping = userCfg.damping;
  if (typeof userCfg.mass === "number") c.mass = userCfg.mass;
  if (typeof userCfg.velocity === "number") c.velocity = userCfg.velocity;
  if (typeof userCfg.restSpeed === "number") c.restSpeed = userCfg.restSpeed;
  if (typeof userCfg.restDelta === "number") c.restDelta = userCfg.restDelta;
  return c;
}

function isMotionValue(v) {
  return !!(v && typeof v === "object" && v.__isMotionValue === true);
}

function createMotionValue(initial) {
  var cur = initial;
  var subs = new Set();
  var prev = initial;
  var mv = {
    __isMotionValue: true,
    get: function() { return cur; },
    getPrevious: function() { return prev; },
    set: function(v) {
      if (v === cur) return;
      prev = cur;
      cur = v;
      subs.forEach(function(s) { try { s(v, prev); } catch (e) { /* noop */ } });
    },
    onChange: function(cb) {
      subs.add(cb);
      return function() { subs.delete(cb); };
    },
    destroy: function() { subs.clear(); },
    // Attach/detach for react state sync
    _reactSubs: subs
  };
  return mv;
}

function useMotionValue(initial) {
  var ref = useRef(null);
  if (ref.current === null) ref.current = createMotionValue(initial);
  return ref.current;
}

function readValue(v) {
  return isMotionValue(v) ? v.get() : v;
}

function numericValue(v) {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    var n = parseFloat(v);
    if (!isNaN(n)) return n;
  }
  return 0;
}

function stepSpring(state, cfg, dtMs) {
  var dt = dtMs / 1000;
  if (dt <= 0) return state;
  var stiffness = cfg.stiffness;
  var damping = cfg.damping;
  var mass = cfg.mass;
  var fSpring = -stiffness * (state.current - state.target);
  var fDamping = -damping * state.velocity;
  var a = (fSpring + fDamping) / mass;
  var vNew = state.velocity + a * dt;
  var cNew = state.current + vNew * dt;
  var atRest = (Math.abs(vNew) <= cfg.restSpeed) && (Math.abs(state.target - cNew) <= cfg.restDelta);
  if (atRest) {
    return { current: state.target, velocity: 0, target: state.target, atRest: true };
  }
  return { current: cNew, velocity: vNew, target: state.target, atRest: false };
}

function useSpring(source, cfg) {
  var mvRef = useRef(null);
  if (mvRef.current === null) mvRef.current = createMotionValue(isMotionValue(source) ? source.get() : readValue(source));
  var springCfg = useMemo(function() { return mergeSpringConfig(cfg); }, [JSON.stringify(cfg || null)]);
  var rafRef = useRef(0);
  var lastTsRef = useRef(0);
  var stateRef = useRef({ current: numericValue(isMotionValue(source) ? source.get() : readValue(source)), velocity: springCfg.velocity || 0, target: numericValue(isMotionValue(source) ? source.get() : readValue(source)), atRest: true });
  useLayoutEffect(function() {
    function onFrame(ts) {
      if (!lastTsRef.current) lastTsRef.current = ts;
      var dt = ts - lastTsRef.current;
      lastTsRef.current = ts;
      var s = stateRef.current;
      var next = stepSpring(s, springCfg, dt);
      stateRef.current = next;
      mvRef.current.set(next.current);
      if (next.atRest) {
        rafRef.current = 0;
        return;
      }
      rafRef.current = requestAnimationFrame(onFrame);
    }
    function setTarget(v) {
      var t = numericValue(v);
      stateRef.current = { current: stateRef.current.current, velocity: stateRef.current.velocity, target: t, atRest: false };
      if (!rafRef.current) {
        lastTsRef.current = 0;
        rafRef.current = requestAnimationFrame(onFrame);
      }
    }
    var unsub = null;
    if (isMotionValue(source)) {
      var src = source;
      setTarget(src.get());
      unsub = src.onChange(function(v) { setTarget(v); });
    } else {
      setTarget(source);
    }
    return function() {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      lastTsRef.current = 0;
      if (unsub) try { unsub(); } catch (e) { /* noop */ }
    };
  }, [isMotionValue(source) ? source : String(readValue(source))]);
  return mvRef.current;
}

function computeTransform(value, from, to) {
  var f0 = numericValue(from[0]);
  var f1 = numericValue(from[1]);
  var t0 = numericValue(to[0]);
  var t1 = numericValue(to[1]);
  var val = numericValue(value);
  var range = f1 - f0;
  if (range === 0) return t0;
  var t = (val - f0) / range;
  return t0 + (t1 - t0) * t;
}

function useTransform(input, domainOrFn, range) {
  var outRef = useRef(null);
  if (outRef.current === null) outRef.current = createMotionValue(0);
  var isFn = typeof domainOrFn === "function";
  useLayoutEffect(function() {
    function compute() {
      var r;
      if (isFn) {
        var arg;
        if (Array.isArray(input)) {
          arg = input.map(function(m) { return isMotionValue(m) ? m.get() : m; });
        } else if (isMotionValue(input)) {
          arg = [input.get()];
        } else {
          arg = [readValue(input)];
        }
        try { r = domainOrFn(arg); } catch(e) { r = ""; }
      } else {
        var fromArr = domainOrFn;
        var toArr = range;
        var val = isMotionValue(input) ? input.get() : readValue(input);
        r = computeTransform(val, fromArr, toArr);
      }
      outRef.current.set(r);
    }
    compute();
    var unsubs = [];
    function attachOne(m) {
      if (isMotionValue(m)) unsubs.push(m.onChange(compute));
    }
    if (Array.isArray(input)) input.forEach(attachOne);
    else attachOne(input);
    return function() {
      unsubs.forEach(function(u) { try { u(); } catch (e) { /* noop */ } });
    };
  }, [isFn || (Array.isArray(input) ? input.length : 1)]);
  return outRef.current;
}

function useMotionTemplate(strings) {
  var values = [];
  for (var i = 1; i < arguments.length; i++) values.push(arguments[i]);
  var outRef = useRef(null);
  if (outRef.current === null) outRef.current = createMotionValue("");
  useLayoutEffect(function() {
    function compute() {
      var s = "";
      for (var i = 0; i < strings.length; i++) {
        s += strings[i];
        if (i < values.length) {
          var v = values[i];
          if (isMotionValue(v)) s += String(v.get());
          else s += String(v);
        }
      }
      outRef.current.set(s);
    }
    compute();
    var unsubs = [];
    values.forEach(function(v) { if (isMotionValue(v)) unsubs.push(v.onChange(compute)); });
    return function() { unsubs.forEach(function(u) { try { u(); } catch (e) { /* noop */ } }); };
  });
  return outRef.current;
}

function toEaseString(cfgOrArray) {
  if (Array.isArray(cfgOrArray)) {
    return "cubic-bezier(" + cfgOrArray.join(",") + ")";
  }
  if (typeof cfgOrArray === "string") {
    switch (cfgOrArray) {
      case "easeInOut": return "cubic-bezier(0.42,0,0.58,1)";
      case "easeIn": return "cubic-bezier(0.42,0,1,1)";
      case "easeOut": return "cubic-bezier(0,0,0.58,1)";
      case "easeInOutSine": return "cubic-bezier(0.37,0,0.63,1)";
      case "linear": return "linear";
      default: return cfgOrArray;
    }
  }
  return "cubic-bezier(0.22,1,0.36,1)";
}

function cssTransitionString(transitionProp, transitionCfg) {
  if (!transitionCfg) return transitionProp + " 0.3s cubic-bezier(0.22,1,0.36,1)";
  var dur = typeof transitionCfg.duration === "number" ? (transitionCfg.duration) + "s" : "0.3s";
  var ease = toEaseString(transitionCfg.ease);
  var delay = typeof transitionCfg.delay === "number" ? (" " + transitionCfg.delay + "s") : "";
  return transitionProp + " " + dur + " " + ease + delay;
}

var motionComponentCache = {};
function __fm_sig(v, seen) {
  if (v === null) return "n:null";
  if (v === undefined) return "u:undefined";
  var t = typeof v;
  if (t === "boolean" || t === "number" || t === "string") return t[0] + ":" + v;
  if (isMotionValue(v)) { try { var g = v.get(); return "mv:" + (typeof g === "number" || typeof g === "string" ? g : ""); } catch (e) { return "mv:"; } }
  if (!seen) seen = [];
  if (seen.indexOf(v) >= 0) return "~";
  seen.push(v);
  if (Array.isArray(v)) {
    var out = "[";
    for (var i = 0; i < v.length; i++) {
      if (i) out += ",";
      out += __fm_sig(v[i], seen);
    }
    out += "]";
    return out;
  }
  if (t === "object") {
    var keys = Object.keys(v).sort();
    var out2 = "{";
    for (var j = 0; j < keys.length; j++) {
      if (j) out2 += ",";
      out2 += keys[j] + ":" + __fm_sig(v[keys[j]], seen);
    }
    out2 += "}";
    return out2;
  }
  return "";
}
function makeMotionComponent(tag) {
  if (motionComponentCache[tag]) return motionComponentCache[tag];
  function Motion(props) {
    var children = props.children;
    var initial = props.initial;
    var animate = props.animate;
    var exit = props.exit;
    var transition = props.transition;
    var refPassed = props.ref;
    var customStyle = props.style || {};
    var className = props.className || undefined;
    var rest = {};
    for (var k in props) {
      if (props.hasOwnProperty(k) && k !== "children" && k !== "initial" && k !== "animate" && k !== "exit" && k !== "transition" && k !== "ref" && k !== "style" && k !== "className") {
        rest[k] = props[k];
      }
    }
    var hostRef = useRef(null);
    var lastKeyframesRef = useRef(null);
    var liveStyleRef = useRef({});
    var mountedRef = useRef(false);
    var animSig = useMemo(function() {
      return __fm_sig(initial) + "|" + __fm_sig(animate) + "|" + __fm_sig(transition) + "|" + __fm_sig(customStyle) + "|" + (className || "");
    }, [initial, animate, transition, customStyle, className]);
    function setRef(el) {
      hostRef.current = el;
      if (typeof refPassed === "function") refPassed(el);
      else if (refPassed && typeof refPassed === "object") { try { refPassed.current = el; } catch (e) { /* noop */ } }
    }
    function resolveAnimateValue() {
      var r = {};
      var src = animate;
      if (src === true || src === undefined) src = {};
      if (src === false) src = initial || {};
      if (isMotionValue(src)) {
        var v = src.get();
        if (typeof v === "object" && v !== null) for (var kk in v) if (v.hasOwnProperty(kk)) r[kk] = v[kk];
      } else if (isObj(src)) {
        for (var kkk in src) if (src.hasOwnProperty(kkk)) r[kkk] = src[kkk];
      }
      return r;
    }
    function applyInitial(el) {
      if (!el) return;
      var s = {};
      var init = initial;
      if (init === false) return;
      if (isObj(init)) for (var k in init) if (init.hasOwnProperty(k)) s[k] = init[k];
      applyStyle(el, s, true);
    }
    function applyStyle(el, keyframes, isInitial) {
      if (!el) return;
      var live = {};
      Object.keys(customStyle).forEach(function(k) { live[k] = readValue(customStyle[k]); });
      Object.keys(keyframes).forEach(function(k) {
        var v = keyframes[k];
        if (Array.isArray(v)) v = v[v.length - 1];
        live[k] = isMotionValue(v) ? v.get() : v;
      });
      var transformParts = [];
      var styleStr = "";
      var cssPropertiesForTransition = new Set();
      Object.keys(live).forEach(function(k) {
        var val = live[k];
        if (k === "transform") {
          if (typeof val === "string" && val.length) transformParts.push(val);
          else if (typeof val === "number") transformParts.push(val + "deg");
        } else if (k === "rotateX" || k === "rotateY" || k === "rotateZ" || k === "rotate") {
          transformParts.push("rotate" + (k === "rotate" ? "Z" : k.replace("rotate", "")) + "(" + (typeof val === "number" ? val + "deg" : val) + ")");
          cssPropertiesForTransition.add("transform");
        } else if (k === "scale" || k === "scaleX" || k === "scaleY") {
          transformParts.push((k === "scale" ? "scale" : k) + "(" + (typeof val === "number" ? val : val) + ")");
          cssPropertiesForTransition.add("transform");
        } else if (k === "x" || k === "y" || k === "z") {
          transformParts.push("translate" + k.toUpperCase() + "(" + (typeof val === "number" ? val + "px" : val) + ")");
          cssPropertiesForTransition.add("transform");
        } else if (k === "opacity" || k === "filter" || k === "height" || k === "marginTop" || k === "background") {
          if (k === "height" && val === "auto") {
            try { el.style[k] = "" + val; } catch (e) { /* noop */ }
          } else {
            try { el.style[k] = "" + val; } catch (e) { /* noop */ }
          }
          cssPropertiesForTransition.add(k);
        } else {
          try { el.style[k] = "" + val; } catch (e) { /* noop */ }
        }
      });
      if (transformParts.length) {
        el.style.transform = transformParts.join(" ");
      }
      liveStyleRef.current = live;
      var hasTransition = !!transition && !isInitial;
      if (hasTransition) {
        var dur = typeof transition.duration === "number" ? transition.duration : 0.3;
        var ease = toEaseString(transition.ease);
        var delay = typeof transition.delay === "number" ? (" " + transition.delay + "s") : "";
        if (transition && typeof transition.repeat === "number") {
          var repeatCount = transition.repeat === Infinity ? "infinite" : "" + transition.repeat;
          var repeatType = (transition.repeatType === "reverse" || transition.repeatType === "mirror") ? "alternate" : "normal";
          var keyframeName = "__fm_kf_" + Math.random().toString(36).slice(2, 9);
          var kf0 = Object.keys(resolveAnimateValue()).map(function(kk) {
            var v0 = initial && initial[kk] !== undefined ? readValue(initial[kk]) : (Array.isArray(animate[kk]) ? animate[kk][0] : readValue(animate[kk]));
            return kk + ":" + (typeof v0 === "number" ? v0 + (kk === "opacity" ? "" : kk === "rotateX" || kk === "rotateY" || kk === "rotate" ? "deg" : kk === "x" || kk === "y" ? "px" : "") : "" + v0);
          }).join(";");
          var kf1 = Object.keys(resolveAnimateValue()).map(function(kk2) {
            var v1 = readValue(animate[kk2]);
            return kk2 + ":" + (typeof v1 === "number" ? v1 + (kk2 === "opacity" ? "" : kk2 === "rotateX" || kk2 === "rotateY" || kk2 === "rotate" ? "deg" : kk2 === "x" || kk2 === "y" ? "px" : "") : "" + v1);
          }).join(";");
          el.style.animation = keyframeName + " " + dur + "s " + ease + delay + " " + repeatCount + " " + repeatType;
          try {
            if (!document.getElementById("__fm_kf_style")) {
              var st = document.createElement("style");
              st.id = "__fm_kf_style";
              document.head.appendChild(st);
            }
            var styleSheet = document.getElementById("__fm_kf_style").sheet;
            styleSheet.insertRule("@keyframes " + keyframeName + " { 0% { " + kf0 + "; } 100% { " + kf1 + "; } }", styleSheet.cssRules.length);
          } catch (e) { /* noop */ }
        } else {
          var transitionItems = [];
          cssPropertiesForTransition.forEach(function(prop) {
            transitionItems.push(prop + " " + dur + "s " + ease + delay);
          });
          if (!transitionItems.length) {
            transitionItems = ["opacity 0.3s cubic-bezier(0.22,1,0.36,1)", "transform 0.3s cubic-bezier(0.22,1,0.36,1)", "filter 0.3s cubic-bezier(0.22,1,0.36,1)", "height 0.3s cubic-bezier(0.22,1,0.36,1)", "margin-top 0.3s cubic-bezier(0.22,1,0.36,1)", "background 0.3s cubic-bezier(0.22,1,0.36,1)"];
          }
          el.style.transition = transitionItems.join(",");
          el.style.willChange = "transform, opacity";
        }
      } else if (isInitial) {
        el.style.transition = "none";
      }
    }
    useLayoutEffect(function() {
      var el = hostRef.current;
      if (!el) return;
      if (!mountedRef.current) {
        applyInitial(el);
        mountedRef.current = true;
      }
      var reqId = null;
      function applyA() {
        reqId = null;
        applyStyle(el, resolveAnimateValue(), false);
      }
      if (initial !== false && !liveStyleRef.current || liveStyleRef.current && Object.keys(liveStyleRef.current).length === 0) {
        reqId = requestAnimationFrame(function() { reqId = requestAnimationFrame(applyA); });
      } else {
        applyA();
      }
      var unsubs = [];
      function watchOne(mv, k) {
        if (!isMotionValue(mv)) return;
        unsubs.push(mv.onChange(function() {
          applyStyle(hostRef.current, resolveAnimateValue(), false);
        }));
      }
      if (isObj(animate)) {
        Object.keys(animate).forEach(function(k) { watchOne(animate[k], k); });
      } else if (isMotionValue(animate)) {
        watchOne(animate, null);
      }
      Object.keys(customStyle).forEach(function(k) { watchOne(customStyle[k], k); });
      return function() {
        if (reqId) cancelAnimationFrame(reqId);
        unsubs.forEach(function(u) { try { u(); } catch (e) { /* noop */ } });
      };
    }, [animSig, initial, animate, transition, customStyle]);
    var passStyle = {};
    Object.keys(customStyle).forEach(function(k) {
      var v = customStyle[k];
      passStyle[k] = isMotionValue(v) ? v.get() : v;
    });
    if (className) passStyle.className = className;
    var STYLE_WHITELIST = { opacity:1, filter:1, height:1, marginTop:1, background:1, transform:1, rotateX:1, rotateY:1, rotateZ:1, rotate:1, scale:1, scaleX:1, scaleY:1, x:1, y:1, z:1, borderRadius:1, width:1, marginLeft:1, marginRight:1, marginBottom:1, padding:1, paddingLeft:1, paddingRight:1, paddingTop:1, paddingBottom:1, color:1, backgroundColor:1, fontWeight:1, fontSize:1, lineHeight:1, letterSpacing:1, boxShadow:1, textShadow:1, border:1, borderColor:1, borderWidth:1, borderStyle:1, borderTopColor:1, borderRightColor:1, borderBottomColor:1, borderLeftColor:1 };
    if (isObj(initial)) {
      Object.keys(initial).forEach(function(k) {
        if (passStyle[k] === undefined && STYLE_WHITELIST[k]) passStyle[k] = readValue(initial[k]);
      });
    }
    var allPass = {};
    for (var rk in rest) if (rest.hasOwnProperty(rk)) allPass[rk] = rest[rk];
    for (var sk in passStyle) if (passStyle.hasOwnProperty(sk)) allPass[sk] = passStyle[sk];
    // Strip motion-only / transform-shorthand props so they are not forwarded
    // to the native DOM element (they are applied imperatively via applyStyle),
    // avoiding React "unknown prop" console warnings.
    var NON_DOM_PROPS = {
      whileHover:1, whileTap:1, whileFocus:1, whileInView:1, whileDrag:1,
      drag:1, dragConstraints:1, dragElastic:1, dragMomentum:1, layout:1,
      layoutId:1, variants:1, custom:1, viewport:1,
      onAnimationStart:1, onAnimationComplete:1,
      rotateX:1, rotateY:1, rotateZ:1, rotate:1,
      scale:1, scaleX:1, scaleY:1, x:1, y:1, z:1,
      transformStyle:1, willChange:1
    };
    Object.keys(allPass).forEach(function(k) {
      if (NON_DOM_PROPS[k]) delete allPass[k];
    });
    allPass.ref = setRef;
    if (children !== undefined) allPass.children = children;
    return createElement(tag, allPass);
  }
  motionComponentCache[tag] = Motion;
  return Motion;
}

var motionProxy = new Proxy(function() {}, {
  get: function(_, prop) {
    if (prop === "__esModule") return true;
    if (typeof prop === "symbol") return undefined;
    return makeMotionComponent(String(prop));
  },
  apply: function() { return makeMotionComponent("div"); }
});

function AnimatePresence(props) {
  var children = props.children;
  var mode = props.mode;
  var initial = props.initial === false ? false : true;
  var rendered = useRef({});
  var exited = useRef({});
  var childArr = [];
  function walk(c) {
    if (c === null || c === undefined) return;
    if (Array.isArray(c)) { c.forEach(walk); return; }
    if (React.isValidElement(c)) childArr.push(c);
  }
  walk(children);
  var nowVisibleMap = {};
  childArr.forEach(function(el) {
    var k = el.key;
    if (k === null || k === undefined) return;
    nowVisibleMap[String(k)] = el;
  });
  var keysNowStr = useMemo(function() {
    return Object.keys(nowVisibleMap).sort().join("|");
  }, [nowVisibleMap]);
  var [visible, setVisible] = useState(nowVisibleMap);
  var exiting = useRef({});
  useLayoutEffect(function() {
    var anyChange = false;
    var enteringKeys = {};
    Object.keys(nowVisibleMap).forEach(function(k) {
      if (visible[k] === undefined) {
        exiting.current[k] = false;
        enteringKeys[k] = nowVisibleMap[k];
        anyChange = true;
      } else if (exiting.current[k]) {
        exiting.current[k] = false;
        anyChange = true;
      }
    });
    var anyExit = false;
    Object.keys(visible).forEach(function(k) {
      if (!nowVisibleMap[k] && !exiting.current[k]) {
        exiting.current[k] = true;
        anyExit = true;
        anyChange = true;
      }
    });
    if (Object.keys(enteringKeys).length) {
      setVisible(function(prev) {
        var changed = false;
        var next = {};
        Object.keys(prev).forEach(function(pk) {
          if (prev[pk] !== undefined) { next[pk] = prev[pk]; }
        });
        Object.keys(enteringKeys).forEach(function(ek) {
          if (next[ek] !== enteringKeys[ek]) {
            next[ek] = enteringKeys[ek];
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
    function sweep() {
      setVisible(function(prev) {
        var anyRemoved = false;
        var next = {};
        Object.keys(prev).forEach(function(k) {
          if (!exiting.current[k]) { next[k] = prev[k]; }
          else { anyRemoved = true; }
        });
        return anyRemoved ? next : prev;
      });
    }
    if (anyExit) {
      var t = setTimeout(sweep, 900);
      return function() { clearTimeout(t); };
    }
  }, [keysNowStr]);
  var allKeys = [];
  var seenKeys = {};
  Object.keys(visible).forEach(function(k) {
    if (!seenKeys[k]) { seenKeys[k] = 1; allKeys.push(k); }
  });
  Object.keys(nowVisibleMap).forEach(function(k) {
    if (!seenKeys[k]) { seenKeys[k] = 1; allKeys.push(k); }
  });
  var outArr = [];
  allKeys.forEach(function(k) {
    // Always render the freshest element for keys still present so that parent
    // re-renders (e.g. controlled input onChange) propagate. Fall back to the
    // cached element only for keys that are exiting.
    var el = nowVisibleMap[k] !== undefined ? nowVisibleMap[k] : visible[k];
    if (el) outArr.push(el);
  });
  return createElement(React.Fragment, null, outArr.length ? outArr : null);
}
function AnimatePresencePassthrough(props) {
  return createElement(React.Fragment, null, props.children || null);
}
var AP = (typeof window === "undefined") ? AnimatePresencePassthrough : AnimatePresence;

module.exports = {
  motion: motionProxy,
  AnimatePresence: AP,
  useMotionValue: useMotionValue,
  useSpring: useSpring,
  useTransform: useTransform,
  useMotionTemplate: useMotionTemplate,
  m: motionProxy
};
module.exports.default = module.exports;
